<template>
<div class="list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="text" ref="search_term" v-model="filters.search_term" placeholder="Search..." @keyup.enter="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters">

		<div v-if="showDetail">
			<div :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save Waiver</div>
			<div v-if="showDetail" class="button neutral" @click="backToList()">Back to List</div>
		</div>

		<div v-else>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.internal_use_only ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('internal_use_only')"></span>
					<label>Unpublished</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.inactive ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('inactive')"></span>
					<label>Inactive</label>
				</li>
			</ul>

			<div class="divider"></div>
			<div v-if="!showDetail" class="button action" @click="addItem()">Create a Waiver</div>

			<div class="divider"></div>
			<div class="extra-menu-item" @click="showWaiverTypes = true">Waiver Types</div>
			<div class="extra-menu-item" @click="showKioskSettings = true">Kiosk Settings</div>
			<div v-if="legacyLink" class="extra-menu-item">
				<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
			</div>

			<div class="divider"></div>
			<div class="extra-menu-item">
				<a :href="getDevEnvironment ? 'https://kiosk.test:9001/kiosk/' : '/kiosk'" target="_blank">Open Customer Kiosk</a>
			</div>

			<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload List</div>
		</div>
	</div>

	<loading v-if="loading" class="loading"></loading>

	<div v-else-if="showDetail" class="list-detail" style="margin: 0 1em;">
		<div class="four-columns">
			<div>
				<label>Waiver Type</label>
				<select v-model="listDetail.waiver_type_id">
					<option value="">Select...</option>
					<option v-for="waiverType in waiverTypes" :value="waiverType.waiver_type_id">{{ waiverType.description }}</option>
				</select>
			</div>
			<div>
				<span v-if="showForMinors" class="minor"><i class="fa fa-check"></i> For minors</span>
			</div>
			<div>
				<checkbox v-model="listDetail.internal_use_only" label="Not Published"/>
			</div>
			<div>
				<checkbox v-model="listDetail.inactive" label="Inactive"/>
			</div>
		</div>
		<label>Content</label>
		<ckeditor :editor="editor" :config="editorConfig" v-model="listDetail.content"></ckeditor>
	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list member-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Waiver Type / Excerpt</th>
					<th class="list">Unpublished</th>
					<th class="list">Inactive</th>
				</tr>
			</thead>
			<tr v-for="(waiver, index) in list" class="clickable-list-item" @click="getItem(waiver)">
				<td class="list">
					{{ waiver.description }}<br>
					{{ waiver.content.replace(/(<([^>]+)>)/gi, "").substring(0,80) }}...
				</td>
				<td class="list"><i v-if="waiver.internal_use_only" class="fa fa-check"></i></td>
				<td class="list"><i v-if="waiver.inactive" class="fa fa-check"></i></td>
			</tr>
		</table>

	</div>

	<div class="modal" v-if="showWaiverTypes">
		<div class="inner">
			<h2>Waiver Types</h2>
			<div class="content">
				<div v-for="(waiverType, index) in waiverTypes" class="three-columns align-center" style="grid-template-columns: 1fr 4.5em 1em;">
					<div>
						<input type="text" v-model="waiverType.description" placeholder="Description" :disabled="!waiverType.client_id">
					</div>
					<div>
						<checkbox v-model="waiverType.minor" label="Minor" :disabled="!waiverType.client_id"/>
					</div>
					<div>
						<i v-if="waiverType.client_id" class="fas fa-trash pointer" @click="waiverTypes.splice(index, 1)"></i>
					</div>
				</div>
				<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showWaiverTypes = false">Cancel</div>
				<div class="button neutral spaced" @click="addWaiverType()">Add</div>
				<div class="button action spaced" @click="saveWaiverTypes()">Save</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showKioskSettings">
		<div class="inner">
			<h2>Kiosk Settings</h2>
			<div class="content">
				<b>Show / Hide Kiosk Sections</b>
				<div class="two-columns">
					<div class="v-spaced">
						<label>Sign a Waiver</label>
						<binary-toggle v-model="kiosk_settings.section_waiver" values="show,hide" labels="Show,Hide" />
					</div>
					<div class="v-spaced">
						<label>Find an Item</label>
						<binary-toggle v-model="kiosk_settings.section_product" values="show,hide" labels="Show,Hide" />
					</div>
					<div class="v-spaced">
						<label>Apply for Membership</label>
						<binary-toggle v-model="kiosk_settings.section_membership" values="show,hide" labels="Show,Hide" />
					</div>
					<div class="v-spaced">
						<label>Customer 4473</label>
						<binary-toggle v-model="kiosk_settings.section_4473" values="show,hide" labels="Show,Hide" />
					</div>
				</div>
				<div class="v-spaced">
					<b>Kiosk Waiver Sign On Screen</b>
					<div class="v-spaced">
						<binary-toggle v-model="kiosk_settings.sign_on_screen" values="enable,disable" labels="Enable,Disable" />
						<div class="message">Note: disable sign on screen to enable signing<br>with a connected signature pad.</div>
					</div>
				</div>
				<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showKioskSettings = false">Cancel</div>
				<div class="button action spaced" @click="saveKioskSettings()">Save</div>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{402:'Waivers Setup', 61:'Online Waivers', 403:'Customer Kiosk'}" :tours="false"></help-popup>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

</div>
</template>

<script>
import BinaryToggle from '@/components/BinaryToggle.vue'
import CKEditor from '@ckeditor/ckeditor5-vue2';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Link from '@ckeditor/ckeditor5-link/src/link';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import List from '@ckeditor/ckeditor5-list/src/list';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import { Undo, Redo } from '@ckeditor/ckeditor5-undo/src/';
import Loading from '@/components/Loading.vue'
import Checkbox from '@/components/Checkbox.vue'
import HelpPopup from '@/components/HelpPopup.vue'
export default {
	name: 'waivers',
	props: ['contact_id'],
    components: {
        'binary-toggle': BinaryToggle,
		'ckeditor': CKEditor.component,
        'loading': Loading,
		'checkbox': Checkbox,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			filters: {},
			waiverTypes: [],
			showWaiverTypes: false,
			showKioskSettings: false,
			kiosk_settings: {},
			modalMessage: '',
			list: [],
			paging: {},
			showDetail: false,
			listDetail: {},
			waiverContent: '',
			saving: false,
			gettingExport: false,
			exportMessage: '',
			legacyLink: '',
			editor: ClassicEditor,
			editorConfig: {
                plugins: [
					Alignment,
                    Bold,
                    Italic,
					Underline,
                    Link,
                    Paragraph,
					Heading,
					List,
					MediaEmbed,
					Undo
                ],
                toolbar: {
                    items: [
                        'bold',
                        'italic',
						'underline',
						'heading',
						'link',
						'alignment:left',
						'alignment:center',
						'alignment:right',
						'numberedList',
						'bulletedList',
						'mediaEmbed',
                        'undo',
                        'redo',
                    ]
                },
				heading: {
		            options: [
		                { model: 'paragraph', title: 'Paragraph' },
		                { model: 'heading1', view: 'h1', title: 'Heading 1' },
						{ model: 'heading2', view: 'h2', title: 'Heading 2' },
		                { model: 'heading3', view: 'h3', title: 'Heading 3' },
					]
				}
            }
		}
	},
	created() {
		this.getListWaiverTypes()
		this.clearFilters()
	},
	computed: {
		canSave() {
			return true
		},
		showForMinors() {
			return this.listDetail.waiver_type_id && this.waiverTypes.find(waiverType => waiverType.waiver_type_id == this.listDetail.waiver_type_id).minor
		}
	},
	methods: {
        theUploadAdapterPlugin(editor) {
			editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
				return new CDSUploadAdapter(loader);
			}
		},
		getListWaiverTypes() {
			this.apiCall({ destination: 'get_list_waiver_types' }).then(obj => {
				this.waiverTypes = obj.waiver_types
			}).catch(err => {
				console.log(err)
			})
		},
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				internal_use_only: 0,
				inactive: 0,
				do_export: 0
			}
			this.getList()
		},
		getList() {
			if (this.loading) { return }
			this.loading = true
			this.apiCall({ destination: 'get_list_waivers', data: this.filters }).then(obj => {
				this.loading = false
				if (obj.legacy_link) {
					this.legacyLink = obj.legacy_link
				}
				if (this.filters.do_export) {
					this.filters.do_export = 0
					var outputFile = obj.file_name
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = "//" + document.location.hostname + "/download.php?file_name=" + obj.file_name + "&output_file_name=" + outputFile
						this.gettingExport = false
						this.exportMessage = ''
					}, 1500)
				} else {
					this.list = obj.list
					if (obj.paging) {
						this.paging = obj.paging
					}
					if (obj.kiosk_settings) {
						this.kiosk_settings = obj.kiosk_settings
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.filters.page = 1
			this.getList()
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItem(item) {
			this.listDetail = this.$copy(item)
			this.showDetail = true
		},
		addItem() {
			this.listDetail = {
				client_id: this.getClientId,
				waiver_type_id: '',
				content: '',
				internal_use_only: 0,
				inactive: 0
			}
			this.showDetail = true
		},
		saveItem() {
			if (this.saving) { return }
			this.messageStatus = ''
			if (!this.listDetail.waiver_type_id) {
				this.messageStatus = 'invalid'
				this.message = 'Waiver Type is required.'
				return false
			}
			if (!this.listDetail.content) {
				this.messageStatus = 'invalid'
				this.message = 'Content is required.'
				return false
			}
			this.saving = true
			this.message = 'Saving...'
			this.apiCall({ destination: 'save_item_waiver', data: this.listDetail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.listDetail.waiver_id = obj.waiver_id
					this.message = 'Saved!'
					setTimeout(() => {
						this.message = ''
					}, 1500)
				} else {
					this.message = obj.message || 'Error saving waiver.'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addWaiverType() {
			this.waiverTypes.push({
				client_id: this.getClientId,
				waiver_type_id: '',
				description: '',
				inactive: 0
			})
			this.modalMessage = ''
		},
		saveWaiverTypes() {
			if (this.saving) { return }
			this.modalMessage = 'Saving...'
			// iterate waiver types, if description is blank and waiver_type_id is blank, remove from array
			var waiverTypes = []
			for (var i = 0; i < this.waiverTypes.length; i++) {
				if (this.waiverTypes[i].waiver_type_id && !this.waiverTypes[i].description) {
					this.modalMessage = 'Description is required for previously saved waiver types.'
				} else if (this.waiverTypes[i].description && this.waiverTypes[i].client_id) {
					waiverTypes.push(this.waiverTypes[i])
				}
			}
			if (waiverTypes.length == 0) {
				this.modalMessage = 'Nothing to save.'
				return false
			}
			this.saving = true
			this.apiCall({ destination: 'save_waiver_types', data: { waiver_types: waiverTypes } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.waiverTypes = obj.waiver_types
					this.modalMessage = 'Waiver types saved!'
					setTimeout(() => {
						this.modalMessage = ''
						this.showWaiverTypes = false
					}, 1500)
				} else {
					this.modalMessage = obj.message || 'Error saving waiver types.'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveKioskSettings() {
			if (this.saving) { return }
			this.modalMessage = 'Saving...'
			this.saving = true
			this.apiCall({ destination: 'save_item_kiosk_settings', data: { kiosk_settings: this.kiosk_settings } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.modalMessage = 'Kiosk settings saved!'
					setTimeout(() => {
						this.modalMessage = ''
						this.showKioskSettings = false
					}, 1500)
				} else {
					this.modalMessage = obj.message || 'Error saving kiosk settings.'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.listDetail = {}
			this.showDetail = false
			this.getList()
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.list.length + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
